import { scaleLinear } from 'd3-scale';

import { calendarDay } from '../../shared/lib/format';
import { AreaChart } from '../../shared/ui/area-chart';
import { formatBytes, type TRetentionUsage } from '../../entities/retention';

const UNITS = ['B', 'kB', 'MB', 'GB', 'TB'] as const;

// "0 GB · 20 GB · 40 GB": every tick in the top tick's unit, whole numbers.
const tickFormat = (top: number) => {
  let unit = 0;
  while (top >= 1000 ** (unit + 1) && unit < UNITS.length - 1) unit += 1;
  return (value: number) => `${Math.round(value / 1000 ** unit)} ${UNITS[unit]}`;
};

export const StorageChart = ({ usage, now }: { usage: TRetentionUsage; now: number }) => {
  const days = usage.daily;
  const peak = Math.max(1, ...days.map((day) => day.totalBytes));
  const ticks = scaleLinear().domain([0, peak]).nice(2).ticks(2);
  const top = ticks[ticks.length - 1] ?? peak;
  const last = days.length - 1;
  const today = new Date(now).toISOString().slice(0, 10);
  const axisAt = [...new Set([0, Math.round(last / 3), Math.round((2 * last) / 3), last])].filter(
    (index) => index >= 0,
  );
  const dayText = (index: number) => {
    const day = days[index]?.day ?? '';
    return index === last && day === today ? 'today' : calendarDay(`${day}T12:00:00Z`, now, 'UTC');
  };

  const tracked = days.find((day) => !day.reconstructed)?.day;
  const rebuilt = days.some((day) => day.reconstructed);
  const estimated = days.some((day) => day.estimated);

  return (
    <>
      <AreaChart
        label={
          usage.growthSince
            ? `Stored since ${calendarDay(`${usage.growthSince}T12:00:00Z`, now, 'UTC')}`
            : 'Stored over the last 30 days'
        }
        points={days.map((day) => ({
          label: calendarDay(`${day.day}T12:00:00Z`, now, 'UTC'),
          value: day.totalBytes,
          estimated: day.estimated,
        }))}
        ticks={ticks.length > 1 ? ticks : [0, top]}
        tickText={tickFormat(top)}
        valueText={formatBytes}
        axisAt={axisAt}
        axisText={dayText}
      />
      {rebuilt && (
        <p className="mt-1 text-meta text-muted">
          {tracked
            ? `Before ${calendarDay(`${tracked}T12:00:00Z`, now, 'UTC')}: reconstructed from stored days`
            : 'Reconstructed from stored days'}
          {estimated
            ? ' — dashed where a cleanup’s bytes can’t be dated, so the total is approximate.'
            : '.'}
        </p>
      )}
    </>
  );
};
